import { filterFileTypes } from "@src/components/modules/Upload/filterFileTypes";
import { UploadDocumentType } from "@src/components/modules/Upload/UploadDocumentType";
import { notifyError } from "@src/shared/notify";
import { ChangeEvent, useCallback } from "react";
import { useDropArea } from "react-use";
import { DropAreaBond, DropAreaState } from "react-use/lib/useDropArea";

interface Props {
  onDocsAdded?: (docs: File[]) => void;
  allowedTypes: UploadDocumentType[];
}

/**
 * A hook for responding to drop events in the document upload area.
 * This hook only handles the drop event, and does not handle the upload itself.
 * It does file type validation via the `allowedTypes` prop.
 */
export function useDocumentDrop({ onDocsAdded, allowedTypes }: Props): {
  bond: DropAreaBond;
  state: DropAreaState;
  selectFile: (event: ChangeEvent<HTMLInputElement>) => void;
} {
  const [bond, state] = useDropArea({
    onFiles: (files: File[]) => {
      const { allowedFiles, errorMessage } = filterFileTypes(
        files,
        allowedTypes,
      );

      if (errorMessage) {
        notifyError(errorMessage);
        return;
      }

      if (onDocsAdded) {
        onDocsAdded(allowedFiles);
      }
    },
    onUri: (uri) => console.log("uri:", uri),
    onText: (text) => console.log("text:", text),
  });

  const selectFile = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;
      if (!files || !files.length) {
        return;
      }
      const { allowedFiles, errorMessage } = filterFileTypes(
        Array.from(files),
        allowedTypes,
      );
      if (errorMessage) {
        notifyError(errorMessage);
        return;
      }

      if (onDocsAdded) {
        onDocsAdded(allowedFiles);
      }
    },
    [allowedTypes, onDocsAdded],
  );

  return {
    bond,
    state,
    selectFile,
  };
}
